import { useQuery } from "@tanstack/react-query";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { CalendarDays, MapPin, ArrowLeft, FileText } from "lucide-react";
import { parseCalendarDate } from "@/lib/dates";

export default function EventDetail() {
  const { eventId } = useParams();
  const { data, isLoading, error } = useQuery({
    queryKey: ["member-event", eventId],
    queryFn: () => api.get(`/events/${eventId}`),
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-32" data-testid="event-detail-loading">
        <div className="w-12 h-0.5 bg-bronze mx-auto animate-pulse" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-32 px-6" data-testid="event-detail-error">
        <p className="text-cream text-sm">{error.message}</p>
      </div>
    );
  }

  const event = data;
  const d = parseCalendarDate(event.date);
  const content = event.content || [];

  return (
    <div className="max-w-3xl mx-auto" data-testid="event-detail-page">
      <Link
        to="/events"
        className="inline-flex items-center gap-1.5 text-xs tracking-[0.15em] uppercase text-lv-muted hover:text-bronze transition-colors mb-8"
        data-testid="event-detail-back"
      >
        <ArrowLeft size={14} />
        Events
      </Link>

      {/* Hero image */}
      {event.image_url && (
        <div className="relative h-48 sm:h-64 overflow-hidden mb-8 border border-lv-border">
          <img
            src={event.image_url}
            alt={event.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-walnut via-transparent to-transparent" />
        </div>
      )}

      {/* Event type label */}
      {event.event_type && (
        <p className="text-[10px] tracking-[0.4em] uppercase text-bronze mb-2">
          {event.event_type}
        </p>
      )}

      <h1
        className="font-serif text-3xl sm:text-4xl text-cream tracking-wide"
        data-testid="event-detail-title"
      >
        {event.title}
      </h1>

      {/* Meta */}
      <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mt-4 mb-8">
        {d && (
          <div className="flex items-center gap-2">
            <CalendarDays size={14} className="text-bronze shrink-0" />
            <span className="text-sm text-sand/80 tracking-wide">
              {d.toLocaleDateString("en-US", {
                weekday: "long",
                month: "long",
                day: "numeric",
                year: "numeric",
              })}
            </span>
          </div>
        )}
        {event.location && (
          <div className="flex items-center gap-2">
            <MapPin size={14} className="text-lv-muted/60 shrink-0" />
            <span className="text-sm text-lv-muted/80 tracking-wide">{event.location}</span>
          </div>
        )}
      </div>

      {/* Description */}
      {event.description && (
        <div className="bg-walnut border border-lv-border p-6 mb-8" data-testid="event-detail-description">
          <p className="text-sm text-sand/80 leading-relaxed whitespace-pre-line">
            {event.description}
          </p>
        </div>
      )}

      {/* Related content */}
      {content.length > 0 && (
        <section data-testid="event-detail-content">
          <div className="flex items-center gap-4 mb-6">
            <div className="h-px flex-1 bg-gradient-to-r from-bronze/30 to-transparent" />
            <span className="text-[11px] tracking-[0.25em] uppercase text-bronze font-medium">
              Related
            </span>
            <div className="h-px flex-1 bg-gradient-to-l from-bronze/30 to-transparent" />
          </div>
          <div className="space-y-3">
            {content.map((c) => (
              <div
                key={c.id}
                className="bg-walnut border border-lv-border p-5"
                data-testid={`event-content-${c.id}`}
              >
                <div className="flex items-start gap-3">
                  <FileText size={14} className="text-lv-muted/50 shrink-0 mt-0.5" />
                  <div>
                    <h3 className="text-sm text-cream tracking-wide font-medium">{c.title}</h3>
                    {c.subtitle && (
                      <p className="text-xs text-sand/60 mt-1 leading-relaxed">{c.subtitle}</p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
